import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowRight, CheckCircle, Zap, Shield, BookOpen, Users, User, Trophy, Sun, Moon, Headphones, GraduationCap, Flame, Medal, Lock, Quote, Star, Twitter, Instagram, Linkedin, Facebook, Menu, X, CreditCard, QrCode, Smartphone, Music2 } from 'lucide-react';


import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import SEO from '../../components/SEO';
import LanguageSwitcher from '../../components/LanguageSwitcher';

export default function Landing() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  // Promotion Valentine's Day - Expire le 28 février 2026 à minuit
  const isPromoActive = new Date() < new Date('2026-03-01T00:00:00');

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    // Check for mobile device reliably
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CitoyenPro",
    "operatingSystem": "Web, iOS, Android",
    "applicationCategory": "EducationalApplication",
    "description": t('landing.hero.description'),
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "CAD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "2400"
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg text-text-main font-sans selection:bg-primary selection:text-white">
      <SEO
        title={t('landing.hero.title')}
        description={t('landing.hero.description')}
        keywords="citoyenneté canadienne, test canada, préparation examen citoyenneté, application citoyenneté, quiz citoyenneté, flashcards"
        canonical="/"
        type="website"
        schema={schema}
      />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${scrolled ? 'bg-dark-bg/95 backdrop-blur-md border-dark-border' : 'bg-transparent border-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 shrink-0">
                <img src="/logo/app-logo.png" alt="CitoyenPro Logo" className="w-full h-full object-contain" />
              </div>
              <span className="text-2xl font-bold text-text-main tracking-tight">CitoyenPro</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/pricing" className="text-sm font-medium text-text-muted hover:text-text-main transition-colors">
                {t('landing.nav.pricing')}
              </Link>
              <Link to="/login" className="text-sm font-medium text-text-muted hover:text-text-main transition-colors">
                {t('landing.nav.login')}
              </Link>
              <LanguageSwitcher />
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <Link to="/signup" className="px-5 py-2.5 bg-primary text-white rounded-full font-bold hover:bg-primary-hover transition-all shadow-lg hover:shadow-primary/20 text-sm">
                {t('landing.nav.start')}
              </Link>
            </div>

            {/* Mobile Actions */}
            <div className="flex md:hidden items-center gap-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-text-main hover:bg-dark-hover rounded-xl transition-colors"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden pt-4 pb-6 px-4 absolute top-full left-0 right-0 bg-dark-bg border-b border-dark-border shadow-2xl animate-in slide-in-from-top-2">
              <div className="flex flex-col space-y-4">
                <div className="p-3">
                  <LanguageSwitcher className="w-full justify-start p-3 hover:bg-dark-hover rounded-xl" />
                </div>
                <Link
                  to="/pricing"
                  className="p-3 rounded-xl hover:bg-dark-hover text-text-main font-medium flex items-center gap-3"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <CreditCard size={20} className="text-primary" />
                  {t('landing.nav.pricing')}
                </Link>
                <Link
                  to="/login"
                  className="p-3 rounded-xl hover:bg-dark-hover text-text-main font-medium flex items-center gap-3"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User size={20} className="text-primary" />
                  {t('landing.nav.login')}
                </Link>
                <Link
                  to="/signup"
                  className="p-3 rounded-xl bg-primary text-white font-bold text-center mt-2 shadow-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('landing.nav.start')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-500/10 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-dark-card border border-dark-border">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              <span className="text-sm font-medium text-text-muted">{t('landing.hero.badge')}</span>
            </div>

            {isPromoActive && (
              <Link to="/pricing" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-bold text-sm hover:bg-primary/20 transition-colors shadow-sm">
                {t('landing.promo.badge')}
              </Link>
            )}
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-text-main mb-8 tracking-tight leading-tight max-w-4xl mx-auto">
            {t('landing.hero.title').split(t('landing.hero.title_gradient'))[0]} <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-400">
              {t('landing.hero.title_gradient')}
            </span>
          </h1>

          <p className="text-xl text-text-muted mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('landing.hero.description')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/signup" className="px-8 py-4 bg-primary text-white text-lg font-bold rounded-full hover:bg-primary-hover transition-all shadow-xl hover:shadow-primary/25 hover:scale-105 flex items-center gap-2">
              {t('landing.hero.cta.free')}
              <ArrowRight size={20} />
            </Link>

            {/* Mobile-only Install Button - JS Based detection for reliability */}
            {isMobile && (
              <button
                onClick={() => (window as any).triggerPWAInstall?.()}
                className="px-8 py-4 bg-dark-card text-text-main text-lg font-bold rounded-full border border-dark-border hover:bg-dark-hover transition-all flex items-center gap-2"
              >
                <Smartphone size={20} className="text-primary" />
                {t('landing.hero.cta.install')}
              </button>
            )}

            <a href="#features" className={`${isMobile ? 'hidden' : 'flex'} px-8 py-4 bg-dark-card text-text-main text-lg font-bold rounded-full border border-dark-border hover:bg-dark-hover transition-all flex items-center gap-2`}>
              {t('landing.hero.cta.discover')}
            </a>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto border-t border-dark-border pt-12">
            {[
              { value: '2400+', label: t('landing.stats.questions') },
              { value: '98%', label: t('landing.stats.success') },
              { value: '5', label: t('landing.stats.audio') },
              { value: '24/7', label: t('landing.stats.access') },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-text-main mb-1">{stat.value}</div>
                <div className="text-text-muted text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-dark-bg/50 relative border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-text-main mb-6">
              {t('landing.features.title')}
            </h2>
            <p className="text-xl text-text-muted max-w-2xl mx-auto">
              {t('landing.features.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: t('landing.features.flashcards.title'),
                desc: t('landing.features.flashcards.desc'),
                color: "text-yellow-400",
                bg: "bg-yellow-400/10"
              },
              {
                icon: CheckCircle,
                title: t('landing.features.quiz.title'),
                desc: t('landing.features.quiz.desc'),
                color: "text-primary",
                bg: "bg-primary/10"
              },
              {
                icon: Headphones,
                title: t('landing.features.audio.title'),
                desc: t('landing.features.audio.desc'),
                color: "text-purple-400",
                bg: "bg-purple-400/10",
                badge: "Premium"
              },
              {
                icon: GraduationCap,
                title: t('landing.features.exam.title'),
                desc: t('landing.features.exam.desc'),
                color: "text-green-400",
                bg: "bg-green-400/10",
                badge: "Premium"
              },
              {
                icon: Trophy,
                title: t('landing.features.stats.title'),
                desc: t('landing.features.stats.desc'),
                color: "text-blue-400",
                bg: "bg-blue-400/10"
              },
              {
                icon: Flame,
                title: t('landing.features.gamification.title'),
                desc: t('landing.features.gamification.desc'),
                color: "text-orange-400",
                bg: "bg-orange-400/10"
              }
            ].map((feature, i) => (
              <div key={i} className="bg-dark-card border border-dark-border rounded-3xl p-8 hover:border-primary/30 transition-all hover:-translate-y-1 group relative overflow-hidden">
                {feature.badge && (
                  <div className="absolute top-4 right-4 bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider border border-primary/20">
                    {feature.badge}
                  </div>
                )}
                <div className={`w-14 h-14 ${feature.bg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`w-7 h-7 ${feature.color}`} />
                </div>
                <h3 className="text-xl font-bold text-text-main mb-4">{feature.title}</h3>
                <p className="text-text-muted leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gamification Highlight Section */}
      <section className="py-24 relative overflow-hidden bg-dark-card/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 mb-6">
                <Flame size={18} className="text-orange-500" />
                <span className="text-sm font-bold text-orange-500 uppercase tracking-wider">{t('landing.gamification.badge')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-text-main mb-6">
                {t('landing.gamification.title')}
              </h2>
              <p className="text-xl text-text-muted mb-8 leading-relaxed">
                {t('landing.gamification.description')}
              </p>
              <ul className="space-y-6">
                {[
                  { icon: Medal, title: t('landing.gamification.item1.title'), desc: t('landing.gamification.item1.desc') },
                  { icon: Flame, title: t('landing.gamification.item2.title'), desc: t('landing.gamification.item2.desc') },
                  { icon: Trophy, title: t('landing.gamification.item3.title'), desc: t('landing.gamification.item3.desc') }
                ].map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <div className="w-12 h-12 bg-dark-bg rounded-xl border border-dark-border flex items-center justify-center shrink-0">
                      <item.icon className="text-text-main" />
                    </div>
                    <div>
                      <h4 className="font-bold text-text-main text-lg">{item.title}</h4>
                      <p className="text-text-muted">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              {/* Abstract Visual Representation of Gamification */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-orange-500/20 blur-3xl rounded-full" />
              <div className="relative bg-dark-bg border border-dark-border rounded-3xl p-8 shadow-2xl">
                <div className="flex items-center justify-between mb-8 border-b border-dark-border pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                    <div>
                      <div className="h-2.5 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-1.5" />
                      <div className="h-2 w-16 bg-gray-100 dark:bg-gray-800 rounded" />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-orange-500 font-bold">
                    <Flame fill="currentColor" /> {t('landing.gamification.mockup.streak')}
                  </div>
                </div>
                <div className="space-y-4">
                  {[1, 2, 3].map((_, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-dark-hover border border-dark-border/50">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${i === 0 ? 'bg-primary/20 text-primary' : i === 1 ? 'bg-green-500/20 text-green-500' : 'bg-blue-500/20 text-blue-500'}`}>
                        {i === 0 ? <Medal size={20} /> : i === 1 ? <CheckCircle size={20} /> : <Zap size={20} />}
                      </div>
                      <div className="flex-1">
                        <div className="h-2 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-1.5" />
                        <div className="h-1.5 w-1/2 bg-gray-100 dark:bg-gray-800 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Testimonials Section */}
      <section className="py-24 bg-dark-bg relative border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-text-main mb-6">
              {t('landing.testimonials.title')}
            </h2>
            <p className="text-xl text-text-muted max-w-2xl mx-auto">
              {t('landing.testimonials.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: t('landing.testimonials.item1.name'),
                role: t('landing.testimonials.item1.role'),
                image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&q=80",
                quote: t('landing.testimonials.item1.quote'),
                stars: 5,
                delay: "delay-0"
              },
              {
                name: t('landing.testimonials.item2.name'),
                role: t('landing.testimonials.item2.role'),
                image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=150&q=80",
                quote: t('landing.testimonials.item2.quote'),
                stars: 5,
                delay: "delay-100"
              },
              {
                name: t('landing.testimonials.item3.name'),
                role: t('landing.testimonials.item3.role'),
                image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=150&q=80",
                quote: t('landing.testimonials.item3.quote'),
                stars: 4,
                delay: "delay-200"
              }
            ].map((testimonial, i) => (
              <div key={i} className={`bg-dark-card border border-dark-border rounded-3xl p-8 relative hover:-translate-y-2 transition-transform duration-300 animate-in fade-in slide-in-from-bottom-8 fill-mode-backwards ${testimonial.delay}`}>
                {/* Quote Icon */}
                <div className="absolute top-8 right-8 text-primary/10">
                  <Quote size={48} fill="currentColor" />
                </div>

                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className={i < testimonial.stars ? "text-yellow-400 fill-yellow-400" : "text-gray-300 dark:text-gray-700"} />
                  ))}
                </div>

                <p className="text-text-main text-lg mb-8 leading-relaxed relative z-10">
                  "{testimonial.quote}"
                </p>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary/20">
                    <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="font-bold text-text-main">{testimonial.name}</h4>
                    <p className="text-sm text-text-muted">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-br from-primary to-orange-900 rounded-3xl p-10 md:p-14 text-center relative overflow-hidden shadow-2xl">
            {/* Deco */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl opacity-50" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                {t('landing.cta.title')}
              </h2>
              <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto font-medium leading-relaxed">
                {t('landing.cta.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup" className="inline-block px-10 py-4 bg-white text-primary text-lg font-bold rounded-full hover:bg-gray-50 transition-all shadow-xl hover:scale-105">
                  {t('landing.cta.free')}
                </Link>
                <Link to="/pricing" className="inline-block px-10 py-4 bg-transparent border-2 border-white text-white text-lg font-bold rounded-full hover:bg-white/10 transition-all">
                  {t('landing.cta.pricing')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PWA Section - More Compact */}
      <section className="py-8 px-6 bg-dark-bg relative overflow-hidden border-t border-dark-border">
        {/* Background Decorative Elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="bg-gradient-to-br from-dark-card to-dark-bg border border-dark-border rounded-[3rem] p-8 md:p-10 shadow-2xl overflow-hidden">
            <div className="flex flex-col lg:flex-row items-center gap-16">

              {/* Left Content */}
              <div className="flex-1 text-center lg:text-left relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-bold mb-8 animate-pulse">
                  <Smartphone size={16} />
                  <span>{t('landing.pwa.badge')}</span>
                </div>

                <h2 className="text-4xl md:text-5xl font-bold text-text-main mb-6 leading-tight tracking-tight">
                  {t('landing.pwa.title').split('CitoyenPro')[0]} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-400">CitoyenPro</span> <br />
                  {t('landing.pwa.title').split('CitoyenPro')[1]}
                </h2>

                <p className="text-text-muted text-lg mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                  {t('landing.pwa.description')}
                </p>

                {/* Instructions with Better Design */}
                <div className="grid sm:grid-cols-2 gap-6 mb-8 text-left">
                  <div className="flex items-start gap-4 p-5 rounded-3xl bg-dark-bg border border-dark-border hover:border-primary/20 transition-colors">
                    <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0 font-extrabold shadow-lg">1</div>
                    <div>
                      <p className="text-text-main font-bold mb-1">{t('landing.pwa.step1.title')}</p>
                      <p className="text-text-muted text-sm italic">{t('landing.pwa.step1.desc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-5 rounded-3xl bg-dark-bg border border-dark-border hover:border-primary/20 transition-colors">
                    <div className="w-10 h-10 rounded-2xl bg-orange-500/10 flex items-center justify-center text-orange-400 shrink-0 font-extrabold shadow-lg">2</div>
                    <div>
                      <p className="text-text-main font-bold mb-1">{t('landing.pwa.step2.title')}</p>
                      <p className="text-text-muted text-sm italic">{t('landing.pwa.step2.desc')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Content - Mockup & QR */}
              <div className="flex-shrink-0 relative w-full lg:w-auto flex flex-col items-center">
                {/* Glow Effect */}
                <div className="absolute inset-x-0 top-0 h-64 bg-primary/10 rounded-full blur-[80px]"></div>

                <div className="relative flex flex-col items-center w-full">
                  {/* QR Code with Logo Container */}
                  <div className="bg-white p-6 rounded-[3rem] shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-gray-100 relative mb-6 w-full max-w-[280px]">
                    <div className="relative p-3 bg-white rounded-3xl border border-gray-50 shadow-inner">
                      <img
                        src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=https%3A%2F%2Fcitoyenpro.nnt-quebec.com%2F&color=0a0a0a&bgcolor=ffffff&qzone=1&ecc=H"
                        alt="QR Code CitoyenPro"
                        className="w-full aspect-square"
                      />
                      {/* Logo in Center Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white p-2 rounded-2xl shadow-xl border border-gray-100">
                          <img src="/logo/app-logo.png" alt="Logo" className="w-10 h-10 md:w-12 md:h-12 object-contain" />
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex items-center justify-center gap-3 py-2.5 px-6 bg-dark-bg/5 rounded-2xl border border-dark-bg/5">
                      <QrCode size={18} className="text-primary animate-bounce" />
                      <span className="text-gray-900 font-extrabold text-sm tracking-tight italic">{t('landing.gamification.mockup.scan')}</span>
                    </div>
                  </div>

                  {/* Platform Compatibility - Replaces Store Badges */}
                  <div className="flex flex-col items-center gap-6 w-full">
                    <div className="flex items-center gap-8 py-4 px-8 bg-dark-hover/30 rounded-3xl border border-dark-border/50 backdrop-blur-sm">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-8 h-8 text-text-muted" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.1 2.48-1.34.03-1.77-.79-3.29-.79-1.53 0-1.99.76-3.27.82-1.31.05-2.32-1.32-3.15-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.36 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                        </svg>
                        <span className="text-[10px] text-text-muted font-bold tracking-widest uppercase">iOS</span>
                      </div>
                      <div className="w-px h-8 bg-dark-border/50"></div>
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-8 h-8 text-text-main" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.523 15.3414C17.7409 15.3414 17.9177 15.1646 17.9177 14.9468V8.95318C17.9177 8.73531 17.7409 8.55853 17.523 8.55853C17.3052 8.55853 17.1284 8.73531 17.1284 8.95318V14.9468C17.1284 15.1646 17.3052 15.3414 17.523 15.3414ZM6.47612 15.3414C6.69399 15.3414 6.87076 15.1646 6.87076 14.9468V8.95318C6.87076 8.73531 6.69399 8.55853 6.47612 8.55853C6.25824 8.55853 6.08147 8.73531 6.08147 8.95318V14.9468C6.08147 15.1646 6.25824 15.3414 6.47612 15.3414ZM17.9571 15.8286H6.04207C5.8242 15.8286 5.64743 16.0054 5.64743 16.2232V19.4623C5.64743 20.3546 6.36668 21.0738 7.25895 21.0738H8.5375V22.9567C8.5375 23.5312 9.0063 24 9.58079 24C10.1553 24 10.6241 23.5312 10.6241 22.9567V21.0738H13.375V22.9567C13.375 23.5312 13.8438 24 14.4183 24C14.9928 24 15.4616 23.5312 15.4616 22.9567V21.0738H16.7402C17.6325 21.0738 18.3517 20.3546 18.3517 19.4623V16.2232C18.3517 16.0054 18.175 15.8286 17.9571 15.8286ZM15.476 7.42588L16.4851 5.67812C16.594 5.4895 16.5293 5.24816 16.3407 5.1393C16.1521 5.03043 15.9107 5.09509 15.8019 5.28371L14.757 7.09341C13.9189 6.70933 12.986 6.49503 12 6.49503C11.0141 6.49503 10.0811 6.70933 9.24302 7.09341L8.19812 5.28371C8.08925 5.09509 7.84791 5.03043 7.65929 5.1393C7.47067 5.24816 7.40602 5.4895 7.51488 5.67812L8.524 7.42588C6.80414 8.27117 5.64743 10.0383 5.64743 12.1009V15.3405H18.3517V12.1009C18.3517 10.0383 17.195 8.27117 15.476 7.42588ZM10.045 11.5367C9.69755 11.5367 9.41492 11.2541 9.41492 10.9067C9.41492 10.5592 9.69755 10.2766 10.045 10.2766C10.3924 10.2766 10.675 10.5592 10.675 10.9067C10.675 11.2541 10.3924 11.5367 10.045 11.5367ZM13.955 11.5367C13.6076 11.5367 13.325 11.2541 13.325 10.9067C13.325 10.5592 13.6076 10.2766 13.955 10.2766C14.3025 10.2766 14.5851 10.5592 14.5851 10.9067C14.5851 11.2541 14.3025 11.5367 13.955 11.5367Z" />
                        </svg>
                        <span className="text-[10px] text-text-muted font-bold tracking-widest uppercase">Android</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <p className="text-[10px] text-primary font-black uppercase tracking-[0.3em]">
                        {t('landing.pwa.direct')}
                      </p>
                      <p className="text-[9px] text-text-muted font-medium italic">
                        {t('landing.pwa.fast')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Footer - Modernized & Mobile Optimized */}
      <footer className="bg-dark-card border-t border-dark-border pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="col-span-2 lg:col-span-1 text-center lg:text-left">
              <Link to="/" className="flex items-center justify-center lg:justify-start gap-3 mb-6">
                <div className="w-10 h-10 shrink-0">
                  <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                </div>
                <span className="text-2xl font-bold text-text-main tracking-tight">CitoyenPro</span>
              </Link>
              <p className="text-text-muted text-sm leading-relaxed mb-6">
                {t('landing.footer.description')}
              </p>
              <div className="space-y-4">
                <p className="text-text-muted text-sm font-bold tracking-widest text-center lg:text-left">{t('landing.footer.social')}</p>
                <div className="flex items-center justify-center lg:justify-start gap-3">
                  <a
                    href="https://www.facebook.com/CitoyenPro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-dark-bg border border-dark-border flex items-center justify-center text-text-muted hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] transition-all duration-300 shadow-sm group"
                    title="Facebook"
                  >
                    <Facebook size={20} className="group-hover:scale-110 transition-transform" />
                  </a>
                  <a
                    href="https://www.tiktok.com/@citoyenpro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-dark-bg border border-dark-border flex items-center justify-center text-text-muted hover:text-white hover:bg-black hover:border-black transition-all duration-300 shadow-sm group"
                    title="TikTok"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="w-5 h-5 fill-current group-hover:scale-110 transition-transform"
                    >
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.9-.32-1.9-.39-2.81-.12-.52.13-1.03.39-1.46.73-.78.57-1.28 1.51-1.35 2.47-.03.8.31 1.6 1.01 2.01.55.33 1.25.43 1.88.24.93-.2 1.68-.96 1.94-1.87.12-.47.11-.97.11-1.45-.01-4.7-.01-9.4-.01-14.1z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center lg:text-left">
              <h4 className="text-text-main font-bold mb-6 uppercase text-xs tracking-[0.2em]">{t('landing.footer.product')}</h4>
              <ul className="space-y-4">
                <li><a href="#features" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.footer.product.features')}</a></li>
                <li><Link to="/pricing" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.nav.pricing')}</Link></li>
                <li><Link to="/login" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.nav.login')}</Link></li>
                <li><Link to="/signup" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.nav.start')}</Link></li>
              </ul>
            </div>

            <div className="text-center lg:text-left">
              <h4 className="text-text-main font-bold mb-6 uppercase text-xs tracking-[0.2em]">{t('landing.footer.resources')}</h4>
              <ul className="space-y-4">
                <li><Link to="/study-guide" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.footer.resources.guide')}</Link></li>
                <li><Link to="/help-center" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.footer.resources.help')}</Link></li>
                <li><Link to="/changelog" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.footer.resources.news')}</Link></li>
                <li><Link to="/legal/contact" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.footer.resources.contact')}</Link></li>
              </ul>
            </div>

            <div className="col-span-2 lg:col-span-1 text-center lg:text-left pt-8 lg:pt-0">
              <h4 className="text-text-main font-bold mb-6 uppercase text-xs tracking-[0.2em]">{t('landing.footer.legal')}</h4>
              <ul className="space-y-4">
                <li><Link to="/legal/privacy" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.footer.legal.privacy')}</Link></li>
                <li><Link to="/legal/terms" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.footer.legal.terms')}</Link></li>
                <li><Link to="/legal/notices" className="text-text-muted hover:text-primary text-sm transition-colors">{t('landing.footer.legal.notices')}</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-dark-border pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-text-muted text-sm">{t('landing.footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
